/**
 * Boombox's agent-embed search config — what shows in the morph
 * palette when ⌘K is pressed inside Boombox.
 *
 *   pages       → JUMP TO section. Sourced from `lib/nav.ts` so
 *                 the sidebar and palette stay in lockstep.
 *   suggestions → SUGGESTED chips (context-aware via fn form)
 *   searchable  → host-app sections while typing. Comms admins get
 *                 SETTINGS (every sidebar Admin page, `settings-nav.ts`),
 *                 TEMPLATES and SENDERS, each opening its detail page
 *                 (`admin-search-kinds.ts` holds the gate). Everyone
 *                 else has none.
 *   crossApp    → "In other apps" sections (Customers, HQ).
 *
 * Per `apps/agent/plans/plan-embed-fixes-11-per-app-search.md`.
 */

import type { AgentEmbedConfig } from '@ordo/agent-embed'
import { createElement, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { adminSearchKinds, warmAdminSearch } from './admin-search-kinds'
import { navFor } from './nav'
import { BOOMBOX_ROUTES } from './routes'

const BASE_CONFIG: AgentEmbedConfig = {
  pages: navFor('palette').map((e) => ({
    id: e.id,
    label: e.label,
    href: e.href,
    icon: createElement(e.icon, { className: 'h-4 w-4' }),
    ...(e.keywords !== undefined ? { keywords: e.keywords } : {}),
  })),
  suggestions: (ctx: { hostContext?: Record<string, unknown>; pathname: string }) => {
    const { pathname } = ctx
    if (pathname === BOOMBOX_ROUTES.SEND || pathname.startsWith('/send/')) {
      return ['Draft a follow-up', 'Pick a template', 'Who should I send to?', 'Schedule for tomorrow morning']
    }
    if (pathname === BOOMBOX_ROUTES.PEOPLE || pathname.startsWith('/contacts/')) {
      return ['Find unsubscribed contacts', 'Build a list from this segment', "Who hasn't opened recently?"]
    }
    if (pathname === BOOMBOX_ROUTES.TEMPLATES || pathname.startsWith('/templates/')) {
      return ['Make a new template', 'What templates are most used?', 'Duplicate this template']
    }
    return ['Send an email', 'Find a contact', 'What did we send last week?', 'Schedule a campaign']
  },
  searchable: [],
  crossApp: {
    apps: [], // wired in Phase A.2
  },
}

/**
 * Boombox's agent config. `isAdmin` is the comms admin check that shows the
 * sidebar's Settings group. Called from the top bar, which renders inside the
 * router (under `RootShell`), so in-app hits route without a page load.
 */
export function useBoomboxAgentConfig({ isAdmin = false }: { isAdmin?: boolean } = {}): AgentEmbedConfig {
  const navigate = useNavigate()
  // Warm the admin lists so the first keystroke is answered from cache
  // rather than racing the palette's per-kind timeout.
  useEffect(() => warmAdminSearch(isAdmin), [isAdmin])
  return useMemo<AgentEmbedConfig>(
    () => ({
      ...BASE_CONFIG,
      searchable: [...BASE_CONFIG.searchable, ...adminSearchKinds(isAdmin)],
      navigate: (href) => void navigate(href),
    }),
    [isAdmin, navigate],
  )
}
